import React, { useState, useEffect } from 'react';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { Splitter, Button, Spin } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined, CloseOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import styles from './Editor.scss';
import FreeFormEditorHeader from './FreeFormEditorHeader';

const mode = {
  python3: 'python',
  javascript: 'javascript',
  c: 'c',
  cpp: 'cpp',
  java: 'java',
  ruby3: 'ruby',
};

function FreeFormEditor({
  output,
  selectedLanguage,
  code,
  updateCode,
  switchLanguage,
  templates,
  submitCode,
  disabled,
  activeTemplate,
  onOutputChange,
}) {
  const theme = useSelector(state => state.events.theme);
  const isLoadingResults = useSelector(state => state.events.isLoadingResults);
  const dispatch = useDispatch();
  const [outputExpanded, setOutputExpanded] = useState(false);
  const [showOutput, setShowOutput] = useState(true);

  const toggleTheme = checked => {
    dispatch({ type: 'SET_THEME', payload: checked ? 'vs-dark' : 'vs' });
  };

  const codeReset = () => {
    updateCode(activeTemplate?.body || '');
  };

  const toggleOutputExpand = () => {
    setOutputExpanded(prev => !prev);
  };

  const closeOutput = () => {
    setShowOutput(false);
    setOutputExpanded(false);
  };

  useEffect(() => {
    if (output) {
      setShowOutput(true);
      onOutputChange();
    }
  }, [output]);

  const isDark = theme === 'vs-dark';

  return (
    <div style={{ height: '100%' }}>
      <Splitter
        layout="vertical"
        size={0}
        style={{ height: '100%', padding: '16px' }}
      >
        <Splitter.Panel
          size={outputExpanded ? '0%' : '70%'}
          min={outputExpanded ? '0%' : '20%'}
          style={{ padding: '0' }}
        >
          <div
            className={styles['c-editor']}
            style={{
              backgroundColor: isDark ? '#1e1e1e' : '#fff',
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
            }}
          >
            <FreeFormEditorHeader
              selectedLanguage={selectedLanguage}
              languageList={templates.map(template => template.language)}
              switchLanguage={switchLanguage}
              codeReset={codeReset}
              submitCode={submitCode}
              disabled={disabled}
              theme={theme}
              toggleTheme={toggleTheme}
              loading={isLoadingResults}
            />
            <div
              className={styles['c-editor__ace']}
              style={{ height: 'calc(100% - 50px)' }}
            >
              {code != null && (
                <MonacoEditor
                  language={mode[selectedLanguage]}
                  theme={theme}
                  onChange={code => updateCode(code)}
                  options={{
                    highlightActiveLine: false,
                    showPrintMargin: false,
                    dragAndDrop: false,
                    tabSize: 4,
                    scrollBeyondLastLine: false,
                    renderFinalNewline: false,
                    minimap: {
                      enabled: false,
                    },
                  }}
                  value={code}
                  height="100%"
                />
              )}
            </div>
          </div>
        </Splitter.Panel>
        {showOutput && (
          <Splitter.Panel
            size={outputExpanded ? '100%' : '30%'}
            min="10%"
            style={{ padding: '0' }}
          >
            <div
              className={styles.custom_io}
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e8e8e8',
                marginBottom: 0,
                backgroundColor: isDark ? '#1e1e1e' : '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  padding: '10px 16px',
                  background: isDark ? '#252526' : '#fbfcfc',
                  borderBottom: `1px solid ${isDark ? '#3e3e42' : '#eee'}`,
                  margin: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3 style={{ margin: 0, color: isDark ? '#cccccc' : '#000' }}>Output</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="text"
                    size="small"
                    icon={outputExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    onClick={toggleOutputExpand}
                    style={{ color: isDark ? '#cccccc' : '#000' }}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={closeOutput}
                    style={{ color: isDark ? '#cccccc' : '#000' }}
                  />
                </div>
              </div>
              {isLoadingResults ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1,
                    flexDirection: 'column',
                  }}
                >
                  <Spin size="large" />
                  <p style={{ marginTop: '16px', color: isDark ? '#cccccc' : '#666' }}>
                    Running code... Please wait.
                  </p>
                </div>
              ) : (
                <>
                  <textarea
                    className={styles.custom_io_body}
                    style={{
                      flex: 1,
                      width: '100%',
                      resize: 'none',
                      border: 'none',
                      padding: '10px',
                      outline: 'none',
                      backgroundColor: isDark ? '#1e1e1e' : '#fff',
                      color: isDark ? '#cccccc' : '#000',
                    }}
                    value={
                      output
                        ? (output.stdout || '') + (output.stderr || '')
                        : 'Click on "Run Code" button to see the output'
                    }
                    readOnly={true}
                  />
                  {output && output.stderr && (
                    <div style={{ color: 'red', padding: '10px' }}>{output.stderr}</div>
                  )}
                </>
              )}
            </div>
          </Splitter.Panel>
        )}
      </Splitter>
    </div>
  );
}

export default FreeFormEditor;
